import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { apiClient, tokenManager } from '../api/client';

interface AppUser {
  id: string;
  email: string;
  name: string;
  avatar?: string;
  isSuperAdmin: boolean;
}

interface AuthContextValue {
  user: AppUser | null;
  loading: boolean;
  isAuthenticated: boolean;
  // Redirects the browser to the backend OIDC login route, which
  // bounces to ctOneAuth. After a successful callback the user
  // lands back on `returnTo` (defaults to "/"). Pass `prompt` to
  // force ctOneAuth to re-prompt instead of silently reusing its
  // existing session ("login" forces re-auth, "select_account"
  // shows the account chooser).
  loginWithOIDC: (returnTo?: string, prompt?: 'login' | 'select_account') => void;
  // Backwards-compatible alias retained for callers that still
  // reference `login`/`register` from the old Supabase shape.
  login: (returnTo?: string) => void;
  register: (returnTo?: string) => void;
  logout: () => Promise<void>;
  updateProfile: (data: Partial<AppUser>) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

function startOidcLogin(returnTo?: string, prompt?: 'login' | 'select_account') {
  const target = returnTo && returnTo.startsWith('/') ? returnTo : '/';
  const params = new URLSearchParams({ returnTo: target });
  if (prompt) params.set('prompt', prompt);
  window.location.href = `/api/auth/login?${params.toString()}`;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);

  // On mount, ask the backend who we are. If there is no active session
  // this returns 401 and the response interceptor will redirect — but
  // we suppress that bounce on the login/portal pages.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get('/auth/me');
        if (!cancelled) setUser(res.data.data.user);
      } catch {
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loginWithOIDC = useCallback(
    (returnTo?: string, prompt?: 'login' | 'select_account') => startOidcLogin(returnTo, prompt),
    [],
  );

  const logout = useCallback(async () => {
    try {
      const res = await apiClient.post('/auth/logout');
      const logoutUrl = res?.data?.data?.logoutUrl as string | undefined;
      tokenManager.setCompanyId(null);
      setUser(null);
      window.location.href = logoutUrl || '/login';
    } catch {
      tokenManager.setCompanyId(null);
      setUser(null);
      window.location.href = '/login';
    }
  }, []);

  const updateProfile = useCallback(async (data: Partial<AppUser>) => {
    const res = await apiClient.post('/auth/update-profile', data);
    setUser(prev => (prev ? { ...prev, ...res.data.data.user } : null));
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated: !!user,
        loginWithOIDC,
        login: loginWithOIDC,
        register: loginWithOIDC,
        logout,
        updateProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
